import React, { useRef, useMemo, useState, useEffect } from 'react'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { AgGridReact } from 'ag-grid-react'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'
import { Box } from '@mui/material'
import { CustomLoadingOverlay, getStringId, sortColDefs } from 'components/common/AgGridUtils'
import { debounce } from 'lodash'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import ResetFiltersButton from 'components/common/ResetFiltersButton'
import { addItemToLS } from 'utils/localStorage'
import SearchBarWithRecent from 'components/common/SearchBarWithRecent'
import AlertHistoryDetailsGrid from 'components/Admin/Organizations/AlertHistoryDetailsGrid'

const AlertHistoryModal = ({ onClose }) => {
  const gridApi = useRef(null)
  const _isMounted = useRef(true)
  const [isLoading, setLoading] = useState(false)
  const { getAgGridTheme, searchBarStyle } = useInnovaTheme()
  const [filterApplied, setFilterApplied] = useState(false)
  const [searchText, setSearchText] = useState('')

  const getAlertHistory = useInnovaAxios({
    url: '/alerts/getAlertHistory',
  })

  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
      gridApi.current = null
    }
  }, [])

  const leftAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'start',
    }),
    [],
  )

  const centerAlignCell = useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }),
    [],
  )

  const debounceSetQuickFilter = debounce((text) => {
    if (gridApi.current) {
      gridApi.current.setGridOption('quickFilterText', text)
    }
  }, 300)

  useEffect(() => {
    debounceSetQuickFilter(searchText)
  }, [searchText]) // eslint-disable-line react-hooks/exhaustive-deps

  const saveColumnState = () => {
    if (gridApi.current) {
      const colLayout = gridApi.current.getColumnState()
      if (colLayout) addItemToLS('alertsHistory', 'colLayout', colLayout)
    }
  }

  const onFirstDataRendered = (params) => {
    if (gridApi.current) gridApi.current.onFilterChanged()
    autoSizeColumns()
  }

  const autoSizeColumns = () => {
    if (gridApi.current === null) {
      return
    }

    gridApi.current?.autoSizeAllColumns()
  }

  const defaultColDef = useMemo(() => {
    return {
      resizable: true,
      sortable: true,
      autoHeight: true,
      editable: false,
      headerClass: 'header-no-padding',
      filter: 'agSetColumnFilter',
      filterParams: {
        excelMode: 'windows',
      },
    }
  }, [])

  const fetchAlertHistory = async () => {
    if (isLoading) return
    setLoading(true)
    const res = await getAlertHistory()

    if (!_isMounted.current) return
    setLoading(false)

    if (res?.error) return
    if (!res?.data) return
    if (!Array.isArray(res.data)) return

    let rowData = []

    res.data.forEach((alert) => {
      if (!alert.hasOwnProperty('alertText')) return
      if (!Array.isArray(alert.alertText)) return
      let data = {
        actualWell: alert?.actualWell,
        recipients: alert?.recipients,
        details: [],
      }
      alert.alertText.forEach((text) => {
        let historyFound = false
        if (text.hasOwnProperty('timestampEmail') && Array.isArray(text.timestampEmails) && text.timestampEmails.length > 0) {
          //if (!Array.isArray(text.timestampEmails)) return
          text?.timestampEmails?.forEach((email) => {
            if (text.sendEmail) {
              let details = {
                alertLevel: text?.alertLevel,
                alertName: text?.alertName,
                alertType: text?.alertType,
                depth: text?.depth,
                value: text?.value,
                alertUnit: text?.alertUnits,
                offsetWell: text?.offsetWell,
                timeStamp: email,
                notification: 'Email',
              }
              data.details.push(details)
            }
          })
          historyFound = true
        }

        if (!historyFound) {
          let details = {
            alertLevel: text?.alertLevel,
            alertName: text?.alertName,
            alertType: text?.alertType,
            depth: text?.depth,
            value: text?.value,
            alertUnit: text?.alertUnits,
            offsetWell: text?.offsetWell,
            timeStamp: text?.timestampEmail,
            notification: 'Email',
          }
          data.details.push(details)
        }
      })
      rowData.push(data)
    })

    gridApi.current.setGridOption('rowData', rowData)
  }

  const onGridReady = (params) => {
    gridApi.current = params.api
    fetchAlertHistory()
  }

  const onFilterChanged = (params) => {
    if (!gridApi.current) return
    if (gridApi.current.isDestroyed()) return

    setFilterApplied(Object.keys(gridApi.current.getFilterModel()).length > 0)
  }

  const getContextMenuItems = (params) => {
    return ['copy']
  }

  const getRowId = useMemo(() => {
    return (params) => {
      return getStringId(params.data?.uid)
    }
  }, [])

  const gridOptions = useMemo(() => {
    return {
      getRowStyle: ({ node }) => (node?.rowPinned ? { fontWeight: 'bold', fontStyle: 'italic' } : 0),
      sideBar: {
        toolPanels: [
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
          },
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
          },
        ],
        defaultToolPanel: '',
        position: 'left',
      },
      onDragStopped: (event) => {
        saveColumnState()
      },
      onColumnVisible: (event) => {
        saveColumnState()
      },
      loadingOverlayComponent: CustomLoadingOverlay,
    }
  }, [])

  let columnDefs = useMemo(
    () => [
      {
        headerName: '',
        colId: '',
        field: '',
        cellStyle: centerAlignCell,
        width: 50,
        minWidth: 50,
        maxWidth: 50,
        pinned: 'left',
        lockPosition: 'left',
        cellRenderer: 'agGroupCellRenderer',
        headerComponent: ResetFiltersButton,
        headerComponentParams: {
          filterApplied: filterApplied,
          gridApi: gridApi,
        },
      },
      {
        headerName: 'Well',
        colId: 'actualWell',
        field: 'actualWell',
        cellStyle: centerAlignCell,
        pinned: 'left',
      },
      {
        headerName: 'User',
        colId: 'recipients',
        field: 'recipients',
        cellStyle: leftAlignCell,
      },
    ],
    [leftAlignCell, centerAlignCell, filterApplied],
  )

  const DetailCellRenderer = (params) => {
    if (!params?.data.details) return null
    if (!Array.isArray(params.data.details)) return null

    return (
      <Box sx={{ width: 'calc(100% - 10px)', height: 500, padding: '5px' }}>
        <AlertHistoryDetailsGrid alertDetails={params.data.details} />
      </Box>
    )
  }

  return (
    <React.Fragment>
      <Dialog
        fullWidth={true}
        maxWidth={'xlg'}
        PaperProps={{
          sx: {
            height: '80vh',
            width: '80vw',
            backgroundColor: 'itemBackground',
          },
        }}
        open={true}
        onClose={() => onClose()}>
        <DialogTitle>Alert History</DialogTitle>
        <Box
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            width: 'calc(100% - 40px)',
            margin: '20px',
          }}>
          <SearchBarWithRecent
            value={searchText}
            onChange={(newSearchTerm) => setSearchText(newSearchTerm)}
            onCancelSearch={() => setSearchText('')}
            style={searchBarStyle()}
            recentKey='alertsHistory'
          />
        </Box>
        <DialogContent>
          <div className={getAgGridTheme()} style={{ height: '100%', width: '100%' }}>
            <AgGridReact
              loading={isLoading}
              columnDefs={sortColDefs(columnDefs, 'alertsHistory')}
              defaultColDef={defaultColDef}
              headerHeight={30}
              onFirstDataRendered={onFirstDataRendered}
              onGridReady={onGridReady}
              getContextMenuItems={getContextMenuItems}
              gridOptions={gridOptions}
              onFilterChanged={onFilterChanged}
              getRowId={getRowId}
              detailCellRenderer={DetailCellRenderer}
              detailRowAutoHeight={true}
              masterDetail={true}
              rowSelection={{
                mode: 'multiRow',
                enableClickSelection: false,
                checkboxes: false,
                headerCheckbox: false,
              }}
              cellSelection={'true'}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => onClose()} color='secondary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}

export default AlertHistoryModal
